import { useEffect, useState } from "react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { ForbiddenPanel } from "../../components/admin/ForbiddenPanel";
import { Sparkline } from "../../components/admin/Sparkline";
import { authedFetch } from "../../lib/api-client";
import { useT } from "../../lib/use-t";

type RoleState = "loading" | "ok" | "forbidden" | "error";

const EVENT_TYPES = [
  "question-gen",
  "prepare-media",
  "turn-grading",
  "report-summary",
  "study-tips",
] as const;

type EventType = (typeof EVENT_TYPES)[number];

type TypeSummary = {
  eventType: EventType;
  count: number;
  totalCostUsd: number;
  avgCostUsd: number;
  avgDurationMs: number;
  trend: number[];
};

type LabelBucket = {
  label: string;
  count: number;
  totalCostUsd: number;
  avgCostUsd: number;
  avgDurationMs: number;
};

type CallTypeRow = {
  eventType: string;
  model: string;
  callCount: number;
  avgDurationMs: number;
  avgCostUsd: number;
  totalDurationMs: number;
  totalCostUsd: number;
};

type EventRow = {
  eventId: string;
  eventType: EventType;
  eventLabel: string | null;
  startedAt: string;
  totalCostUsd: number;
  callCount: number;
  totalTokens: number;
};

type EconomicsResponse = {
  days: number;
  byType: TypeSummary[];
  latency: CallTypeRow[];
  recent: EventRow[];
  breakdowns: Partial<Record<EventType, LabelBucket[]>>;
};

type LoadState =
  | { status: "loading" }
  | { status: "ok"; data: EconomicsResponse }
  | { status: "error"; message: string };

const DAY_OPTIONS = [7, 14, 30, 60, 90];

function fmtUsd(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

function fmtMs(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (n < 1000) return `${Math.round(n)}ms`;
  return `${(n / 1000).toFixed(1)}s`;
}

function fmtTokens(n: number): string {
  if (n < 1000) return `${n}`;
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

export function AdminEconomicsPage() {
  const t = useT();
  const [roleState, setRoleState] = useState<RoleState>("loading");
  const [days, setDays] = useState(30);
  const [load, setLoad] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    authedFetch("/admin/users")
      .then((r) => {
        if (cancelled) return;
        if (r.status === 403) setRoleState("forbidden");
        else if (r.ok) setRoleState("ok");
        else setRoleState("error");
      })
      .catch(() => {
        if (!cancelled) setRoleState("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (roleState !== "ok") return;
    let cancelled = false;
    setLoad({ status: "loading" });
    authedFetch(`/admin/economics?days=${days}`)
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (cancelled) return;
        if (!r.ok || !body?.ok) {
          setLoad({
            status: "error",
            message: body?.error?.message ?? `Server returned ${r.status}.`,
          });
          return;
        }
        setLoad({ status: "ok", data: body.data });
      })
      .catch((err) => {
        if (cancelled) return;
        setLoad({
          status: "error",
          message: err instanceof Error ? err.message : "Onbekende fout.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [roleState, days]);

  return (
    <AdminLayout>
      {roleState === "loading" ? (
        <p className="text-sm text-[var(--ink-faint)]">{t("chrome.common.loadingEllipsis")}</p>
      ) : roleState === "forbidden" ? (
        <ForbiddenPanel />
      ) : (
        <div className="space-y-8">
          <header className="flex flex-wrap items-end gap-4">
            <div className="space-y-2 flex-1">
              <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
                {t("admin.economics.eyebrow")}
              </p>
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                {t("admin.economics.title")}
              </h1>
              <p className="text-sm text-[var(--ink-muted)] leading-relaxed max-w-2xl">
                {t("admin.economics.description")}
              </p>
            </div>
            <label className="flex items-center gap-2 text-xs font-mono-badge uppercase tracking-wider text-[var(--ink-faint)]">
              {t("admin.economics.periodLabel")}
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="rounded-md border border-[var(--border)] bg-[var(--panel-soft)] px-2 py-1 text-[var(--ink)] text-sm font-sans"
              >
                {DAY_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d}d
                  </option>
                ))}
              </select>
            </label>
          </header>

          {load.status === "loading" ? (
            <p className="text-sm text-[var(--ink-faint)]">{t("admin.economics.loadingFigures")}</p>
          ) : load.status === "error" ? (
            <p
              role="alert"
              className="rounded-md border border-[var(--fail)] bg-[var(--fail)]/10 px-3 py-2 text-sm text-[var(--fail)]"
            >
              {load.message}
            </p>
          ) : (
            <Body data={load.data} />
          )}
        </div>
      )}
    </AdminLayout>
  );
}

function Body({ data }: { data: EconomicsResponse }) {
  const t = useT();
  const grandTotal = data.byType.reduce((s, r) => s + r.totalCostUsd, 0);
  return (
    <div className="space-y-8">
      <section aria-labelledby="by-type" className="space-y-3">
        <h2
          id="by-type"
          className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge"
        >
          {t("admin.economics.byTypeHeadingFmt", { total: fmtUsd(grandTotal), days: data.days })}
        </h2>
        <div className="overflow-x-auto rounded-lg border border-[var(--border)] bg-[var(--panel)]">
          <table className="w-full text-sm">
            <thead className="text-xs font-mono-badge uppercase tracking-wider text-[var(--ink-faint)] border-b border-[var(--border)]">
              <tr>
                <th className="text-left px-3 py-2">{t("admin.economics.tableColType")}</th>
                <th className="text-right px-3 py-2">{t("admin.economics.tableColEvents")}</th>
                <th className="text-right px-3 py-2">{t("admin.economics.tableColTotal")}</th>
                <th className="text-right px-3 py-2">{t("admin.economics.tableColAvgPerEvent")}</th>
                <th className="text-right px-3 py-2">{t("admin.economics.tableColAvgDuration")}</th>
                <th className="text-left px-3 py-2 w-36">{t("admin.economics.tableColTrend")}</th>
              </tr>
            </thead>
            <tbody>
              {data.byType.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-6 text-center text-[var(--ink-faint)] font-serif-italic"
                  >
                    {t("admin.economics.noEventsInPeriod")}
                  </td>
                </tr>
              ) : (
                data.byType.map((r) => (
                  <tr
                    key={r.eventType}
                    className="border-b border-[var(--border)]/60 last:border-b-0"
                  >
                    <td className="px-3 py-2 font-mono-badge text-xs text-[var(--ink)]">
                      {r.eventType}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink-muted)]">
                      {r.count}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink)]">
                      {fmtUsd(r.totalCostUsd)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink-muted)]">
                      {fmtUsd(r.avgCostUsd)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink-muted)]">
                      {fmtMs(r.avgDurationMs)}
                    </td>
                    <td className="px-3 py-2">
                      {r.trend.length > 1 ? <Sparkline values={r.trend} /> : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {Object.entries(data.breakdowns).map(([typ, buckets]) => {
        if (!buckets || buckets.length === 0) return null;
        return (
          <section key={typ} aria-labelledby={`bd-${typ}`} className="space-y-3">
            <h2
              id={`bd-${typ}`}
              className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge"
            >
              {t("admin.economics.breakdownPerLabelFmt", { type: typ })}
            </h2>
            <div className="overflow-x-auto rounded-lg border border-[var(--border)] bg-[var(--panel)]">
              <table className="w-full text-sm">
                <thead className="text-xs font-mono-badge uppercase tracking-wider text-[var(--ink-faint)] border-b border-[var(--border)]">
                  <tr>
                    <th className="text-left px-3 py-2">{t("admin.economics.tableColLabel")}</th>
                    <th className="text-right px-3 py-2">{t("admin.economics.tableColCount")}</th>
                    <th className="text-right px-3 py-2">{t("admin.economics.tableColTotal")}</th>
                    <th className="text-right px-3 py-2">{t("admin.economics.tableColAvgPerEvent")}</th>
                    <th className="text-right px-3 py-2">{t("admin.economics.tableColAvgDuration")}</th>
                  </tr>
                </thead>
                <tbody>
                  {buckets.map((b) => (
                    <tr
                      key={b.label}
                      className="border-b border-[var(--border)]/60 last:border-b-0"
                    >
                      <td className="px-3 py-2 font-mono-badge text-xs text-[var(--ink)]">
                        {b.label}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink-muted)]">
                        {b.count}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink)]">
                        {fmtUsd(b.totalCostUsd)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink-muted)]">
                        {fmtUsd(b.avgCostUsd)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink-muted)]">
                        {fmtMs(b.avgDurationMs)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}

      <section aria-labelledby="latency" className="space-y-3">
        <h2
          id="latency"
          className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge"
        >
          {t("admin.economics.latencyHeading")}
        </h2>
        <div className="overflow-x-auto rounded-lg border border-[var(--border)] bg-[var(--panel)]">
          <table className="w-full text-sm">
            <thead className="text-xs font-mono-badge uppercase tracking-wider text-[var(--ink-faint)] border-b border-[var(--border)]">
              <tr>
                <th className="text-left px-3 py-2">{t("admin.economics.tableColEvent")}</th>
                <th className="text-left px-3 py-2">{t("admin.economics.tableColModel")}</th>
                <th className="text-right px-3 py-2">{t("admin.economics.tableColCalls")}</th>
                <th className="text-right px-3 py-2">{t("admin.economics.tableColAvgDuration")}</th>
                <th className="text-right px-3 py-2">{t("admin.economics.tableColCost")}</th>
                <th className="text-right px-3 py-2">{t("admin.economics.tableColTotalCost")}</th>
              </tr>
            </thead>
            <tbody>
              {data.latency.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-6 text-center text-[var(--ink-faint)] font-serif-italic"
                  >
                    {t("admin.economics.noCallsInPeriod")}
                  </td>
                </tr>
              ) : (
                data.latency.map((r) => (
                  <tr
                    key={`${r.eventType}-${r.model}`}
                    className="border-b border-[var(--border)]/60 last:border-b-0"
                  >
                    <td className="px-3 py-2 font-mono-badge text-xs text-[var(--ink-muted)]">
                      {r.eventType}
                    </td>
                    <td className="px-3 py-2 font-mono-badge text-xs text-[var(--ink)] truncate max-w-xs">
                      {r.model}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink-muted)]">
                      {r.callCount}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink)]">
                      {fmtMs(r.avgDurationMs)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink-muted)]">
                      {fmtUsd(r.avgCostUsd)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink)]">
                      {fmtUsd(r.totalCostUsd)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="recent" className="space-y-3">
        <h2
          id="recent"
          className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge"
        >
          {t("admin.economics.recentEventsHeading")}
        </h2>
        <div className="overflow-x-auto rounded-lg border border-[var(--border)] bg-[var(--panel)]">
          <table className="w-full text-sm">
            <thead className="text-xs font-mono-badge uppercase tracking-wider text-[var(--ink-faint)] border-b border-[var(--border)]">
              <tr>
                <th className="text-left px-3 py-2">{t("admin.economics.tableColWhen")}</th>
                <th className="text-left px-3 py-2">{t("admin.economics.tableColTypeLabel")}</th>
                <th className="text-right px-3 py-2">{t("admin.economics.tableColCalls")}</th>
                <th className="text-right px-3 py-2">{t("admin.economics.tableColTokens")}</th>
                <th className="text-right px-3 py-2">{t("admin.economics.tableColCost")}</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-6 text-center text-[var(--ink-faint)] font-serif-italic"
                  >
                    {t("admin.economics.noRecentEvents")}
                  </td>
                </tr>
              ) : (
                data.recent.map((e) => (
                  <tr
                    key={e.eventId}
                    className="border-b border-[var(--border)]/60 last:border-b-0"
                  >
                    <td className="px-3 py-2 font-mono-badge text-xs text-[var(--ink-muted)] tabular-nums">
                      {new Date(e.startedAt).toLocaleString("nl-NL", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="px-3 py-2 font-mono-badge text-xs text-[var(--ink)]">
                      {e.eventType}
                      {e.eventLabel ? ` · ${e.eventLabel}` : ""}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink-muted)]">
                      {e.callCount}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink-muted)]">
                      {fmtTokens(e.totalTokens)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-badge tabular-nums text-[var(--ink)]">
                      {fmtUsd(e.totalCostUsd)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
